// src/components/admin/AdminAnalytics.js
import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { formatEurCheckout } from '../../lib/currency';

const S_COLORS = { pending:'#f0a500', paid:'#4cca74', processing:'#4a9edd', shipped:'#4a9edd', delivered:'#4cca74', cancelled:'#e05050' };

export default function AdminAnalytics() {
  const [data, setData] = useState({ totalRevenue:0, totalOrders:0, totalCustomers:0, avgOrderValue:0, weeklyRevenue:[], topProducts:[], ordersByStatus:{} });
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(7);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    const from = new Date();
    from.setDate(from.getDate() - range);

    const [orders, customers] = await Promise.all([
      supabase.from('orders').select('total_eur,status,created_at,items').gte('created_at', from.toISOString()),
      supabase.from('profiles').select('id', { count:'exact' }).eq('role','customer'),
    ]);

    const paid = (orders.data||[]).filter(o => o.status === 'paid');
    const totalRevenue   = paid.reduce((s,o) => s + Number(o.total_eur), 0);
    const totalOrders    = orders.data?.length || 0;
    const avgOrderValue  = paid.length > 0 ? totalRevenue / paid.length : 0;

    const days = [];
    for (let i = range - 1; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i);
      const dayStr = d.toISOString().slice(0, 10);
      const dayRevenue = paid.filter(o => o.created_at.slice(0,10) === dayStr).reduce((s,o) => s + Number(o.total_eur), 0);
      days.push({ date: d.toLocaleDateString('en-GB', { month:'short', day:'numeric' }), revenue: dayRevenue });
    }

    const statusCounts = {};
    (orders.data||[]).forEach(o => { statusCounts[o.status] = (statusCounts[o.status]||0) + 1; });

    const productCounts = {};
    (orders.data||[]).forEach(o => {
      (o.items||[]).forEach(item => { productCounts[item.name] = (productCounts[item.name]||0) + (item.qty||1); });
    });
    const topProducts = Object.entries(productCounts).sort((a,b) => b[1]-a[1]).slice(0,5).map(([name,count]) => ({ name, count }));

    setData({ totalRevenue, totalOrders, avgOrderValue, weeklyRevenue:days, topProducts, ordersByStatus:statusCounts, totalCustomers: customers.count||0 });
    setLoading(false);
  }, [range]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  const maxRevenue = Math.max(...data.weeklyRevenue.map(d => d.revenue), 1);

  if (loading) return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:300 }}><div className="spinner" /></div>;

  return (
    <div className="page-container" style={{ padding:'32px 24px' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <h2 style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:'1.8rem', letterSpacing:'.05em' }}>Analytics</h2>
        <div style={{ display:'flex', gap:8 }}>
          {[7,14,30].map(r => (
            <button key={r} onClick={() => setRange(r)} style={{ padding:'6px 16px', border:'none', borderRadius:20, background: range===r?'#4cca74':'#111', color: range===r?'#000':'#555', fontFamily:"'Barlow Condensed',sans-serif", fontSize:'.78rem', fontWeight:700, cursor:'pointer' }}>
              {r}d
            </button>
          ))}
        </div>
      </div>

      {/* Metrics */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:12, marginBottom:24 }}>
        {[
          { label:'Revenue', value:formatEurCheckout(data.totalRevenue), green:true },
          { label:'Orders',  value:data.totalOrders },
          { label:'Avg Order', value:formatEurCheckout(data.avgOrderValue) },
          { label:'Customers', value:data.totalCustomers },
        ].map((m,i) => (
          <div key={i} style={{ background:'#111', borderRadius:14, padding:'18px 20px', border:'1px solid rgba(255,255,255,.07)' }}>
            <div style={{ fontSize:'.7rem', color:'#555', fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:'.1em', textTransform:'uppercase', marginBottom:6 }}>{m.label}</div>
            <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:'1.6rem', letterSpacing:'.04em', color: m.green?'#4cca74':'#f4f4f4' }}>{m.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:16, marginBottom:16 }}>
        {/* Revenue chart */}
        <div style={{ background:'#111', borderRadius:16, padding:20, border:'1px solid rgba(255,255,255,.07)' }}>
          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:'.72rem', fontWeight:700, letterSpacing:'.15em', textTransform:'uppercase', color:'#4cca74', marginBottom:16 }}>Daily Revenue (EUR)</div>
          <div style={{ display:'flex', alignItems:'flex-end', gap:4, height:120 }}>
            {data.weeklyRevenue.map((d,i) => (
              <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                <div style={{ width:'100%', borderRadius:'4px 4px 0 0', background: d.revenue>0?'#4cca74':'#1a1a1a', height:`${Math.max(4,(d.revenue/maxRevenue)*100)}px`, transition:'height .4s ease', position:'relative' }}>
                  {d.revenue > 0 && (
                    <div style={{ position:'absolute', bottom:'100%', left:'50%', transform:'translateX(-50%)', fontSize:'7px', color:'#4cca74', whiteSpace:'nowrap', marginBottom:2 }}>
                      €{d.revenue.toFixed(0)}
                    </div>
                  )}
                </div>
                <div style={{ fontSize:'7px', color:'#444', writingMode:'vertical-rl', transform:'rotate(180deg)', height:20, textAlign:'center' }}>{d.date}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Status breakdown */}
        <div style={{ background:'#111', borderRadius:16, padding:20, border:'1px solid rgba(255,255,255,.07)' }}>
          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:'.72rem', fontWeight:700, letterSpacing:'.15em', textTransform:'uppercase', color:'#4cca74', marginBottom:16 }}>Orders by Status</div>
          {Object.entries(data.ordersByStatus).map(([status, count]) => (
            <div key={status} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
              <div style={{ width:8, height:8, borderRadius:'50%', background:S_COLORS[status]||'#888', flexShrink:0 }} />
              <div style={{ flex:1, fontFamily:"'Barlow Condensed',sans-serif", fontSize:'.82rem', color:'#ccc', textTransform:'capitalize' }}>{status}</div>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:'1rem', color:S_COLORS[status]||'#888' }}>{count}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Top products */}
      {data.topProducts.length > 0 && (
        <div style={{ background:'#111', borderRadius:16, padding:20, border:'1px solid rgba(255,255,255,.07)' }}>
          <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:'.72rem', fontWeight:700, letterSpacing:'.15em', textTransform:'uppercase', color:'#4cca74', marginBottom:14 }}>Top Products</div>
          {data.topProducts.map((p,i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:12, marginBottom:10 }}>
              <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:'1.1rem', color:'#333', width:20, flexShrink:0 }}>{i+1}</div>
              <div style={{ flex:1, fontSize:'.85rem', color:'#ccc', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.name}</div>
              <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:'.82rem', fontWeight:700, color:'#4cca74', flexShrink:0 }}>{p.count} sold</div>
              <div style={{ width:80, background:'#1a1a1a', borderRadius:4, height:6, flexShrink:0 }}>
                <div style={{ height:'100%', background:'#4cca74', borderRadius:4, width:`${(p.count/data.topProducts[0].count)*100}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}